import {
  ACCOUNT_FETCH_FAILED,
  ACCOUNT_FETCH_REQUESTED,
  ACCOUNT_FETCH_SUCCEEDED,
  LOG_IN_REQUESTED,
  LOG_IN_SUCCEEDED,
  LOG_OUT,
  LOG_IN_FAILED,
  SIGN_UP_SUCCEEDED,
  SIGN_UP_FAILED,
  PASSWORD_RESET_SUCCEEDED,
  PASSWORD_RESET_FAILED,
  CLEAR_MESSAGING,
  SIGN_UP_REQUESTED,
  PASSWORD_RESET_REQUESTED,
} from './reducer';

import { fetchAccount, logUserIn, signUp, logUserOut, resetPassword } from '../../utils/api';
import { removeToken } from '../../utils/useFlyerAuthToken';
import fastpanel, { alias, identify } from '../../utils/fastpanel';

export interface LogInPayloadProps {
  email_address: string;
  password: string;
}

export interface SignUpPayloadProps {
  allow_marketing: boolean;
  password: string;
  user_agent: string;
  email_address: string;
}

export interface ForgotPasswordPayloadProps {
  email_address: string;
}

class AuthenticationError extends Error {
  authInfo: any;

  constructor(message: string, authInfo?: any) {
    super(message);
    this.name = message;
    this.authInfo = authInfo;
  }
}

const LOG_IN_ERROR_MESSAGING =
  'An error occurred when trying to log in. Please try again or reach out to help@paperlesspost.com';
const SIGN_UP_ERROR_MESSAGING =
  'An error occurred when trying to sign up. Please try again or reach out to help@paperlesspost.com';
const RESET_PASSWORD_SUCCESS_MESSAGING =
  'If an account exists for your email, we will email you instructions for resetting your password within ten minutes. If you do not receive a password reset email, please contact help@paperlesspost.com';
const RESET_PASSWORD_ERROR_MESSAGING =
  'An error occurred when trying to reset your password.  Please try again or reach out to help@paperlesspost.com';

export async function fetchUserAccount(dispatch: any) {
  try {
    dispatch({ type: ACCOUNT_FETCH_REQUESTED });

    const response = await fetchAccount();
    const data = await response.json();

    if (data.status === 'success' && data.account.id && data.account.id !== -1) {
      dispatch({ type: ACCOUNT_FETCH_SUCCEEDED, payload: data });
      return data;
    } else {
      dispatch({ type: ACCOUNT_FETCH_FAILED });
    }
  } catch (error) {
    dispatch({ type: ACCOUNT_FETCH_FAILED });
  }
}

export async function logInUser(dispatch: any, logInPayload: LogInPayloadProps) {
  try {
    dispatch({ type: LOG_IN_REQUESTED });

    const response = await logUserIn({
      account: logInPayload,
      remember_me: true,
    });
    const responseData = await response.json();
    const { account, duo_auth_required, message, status } = responseData;

    if (status === 'success') {
      if (duo_auth_required) {
        // If this is an admin logging into production and they need
        // duo auth, forward them to the duo url provided in the response
        window.location.href = responseData.session_path;
      } else if (account.id && account.id !== -1) {
        identify(account.id);
        fastpanel({ name: 'pbr_log_in_succeeded', data: { id: 'email ' } });
        dispatch({ type: LOG_IN_SUCCEEDED, payload: responseData });
        return responseData;
      }
    } else if (status === 'error') {
      throw new AuthenticationError(LOG_IN_FAILED, { message });
    } else {
      // The api returns a 200 even when you are unauthorized.
      throw new AuthenticationError(LOG_IN_FAILED);
    }
  } catch (error: any) {
    dispatch({
      type: LOG_IN_FAILED,
      payload: {
        error: 'error',
        message: error?.authInfo?.message || LOG_IN_ERROR_MESSAGING,
      },
    });
  }
}

export async function signUpUser(dispatch: any, signUpPayload: SignUpPayloadProps) {
  const { allow_marketing, email_address, password, user_agent } = signUpPayload;

  try {
    dispatch({ type: SIGN_UP_REQUESTED });

    const response = await signUp({
      account: {
        allow_marketing,
        password,
        user_agent,
      },
      email_address,
    });
    const data = await response.json();
    const { account, message, status } = data;

    // Why do we do this? Because the api returns a 200 even when
    // you are unauthorized. So you have to keep this, no matter
    // how much it pains you.
    if (status === 'success' && account.id && account.id !== -1) {
      alias(account.id);
      identify(account.id);
      fastpanel({ name: 'pbr_sign_up_succeeded', data: { id: 'email' } });

      dispatch({ type: SIGN_UP_SUCCEEDED, payload: data });
      return data;
    } else if (status === 'error') {
      const loggedIn = await logInUser(dispatch, { email_address, password });

      if (!loggedIn) {
        throw new AuthenticationError(SIGN_UP_FAILED, { message });
      }
    } else {
      // The api returns a 200 even when you are unauthorized.
      throw new AuthenticationError(SIGN_UP_FAILED);
    }
  } catch (error: any) {
    dispatch({
      type: SIGN_UP_FAILED,
      payload: {
        error: 'error',
        message: error?.authInfo?.message || SIGN_UP_ERROR_MESSAGING,
      },
    });
  }
}

export async function forgotPassword(
  dispatch: any,
  forgotPasswordPayload: ForgotPasswordPayloadProps
) {
  try {
    dispatch({ type: PASSWORD_RESET_REQUESTED });

    const response = await resetPassword(forgotPasswordPayload);
    const data = await response.json();

    fastpanel({ name: 'pbr_reset_password_succeeded' });

    // Always send success message info to make it harder to phish to see what accounts exist...
    dispatch({
      type: PASSWORD_RESET_SUCCEEDED,
      payload: {
        message: RESET_PASSWORD_SUCCESS_MESSAGING,
      },
    });

    return data;
  } catch (error) {
    dispatch({
      type: PASSWORD_RESET_FAILED,
      payload: {
        error: 'failed',
        message: RESET_PASSWORD_ERROR_MESSAGING,
      },
    });
  }
}

export async function logOut(dispatch: any) {
  removeToken();

  await logUserOut();

  fastpanel({ name: 'pbr_log_out_requested' });

  dispatch({ type: LOG_OUT });
}

export async function clearMessaging(dispatch: any) {
  dispatch({ type: CLEAR_MESSAGING });
}
